import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit2, Trash2, CreditCard } from 'lucide-react'
import { masterDataApi } from '../../lib/api'
import type { PaymentMethodPayload } from '../../lib/api'
import type { PaymentMethod, PaymentMethodType } from '../../types'
import DataTable, { Column } from '../../components/shared/DataTable'
import LoadingState from '../../components/shared/LoadingState'
import EmptyState from '../../components/shared/EmptyState'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { tx } from '../../lib/i18n'

// ─── type metadata ──────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<PaymentMethodType, string> = {
  bank_transfer: 'โอนเงิน (Bank Transfer)',
  promptpay: 'พร้อมเพย์ (PromptPay)',
  credit_card: 'บัตรเครดิต (Credit Card)',
  cash: 'เงินสด (Cash)',
}

const TYPE_BADGE: Record<PaymentMethodType, string> = {
  bank_transfer: 'bg-blue-100 text-blue-700',
  promptpay: 'bg-teal-100 text-teal-700',
  credit_card: 'bg-purple-100 text-purple-700',
  cash: 'bg-amber-100 text-amber-700',
}

type FieldDef = { key: keyof PaymentMethodPayload; label: string; required: boolean; placeholder?: string }

// Which fields to render (and require) for each payment-method type.
const FIELDS_BY_TYPE: Record<PaymentMethodType, FieldDef[]> = {
  bank_transfer: [
    { key: 'bankName', label: tx('Bank Name'), required: true, placeholder: 'e.g. KBank' },
    { key: 'accountName', label: tx('Account Name'), required: true },
    { key: 'accountNumber', label: tx('Account Number'), required: true },
    { key: 'bankBranch', label: tx('Branch'), required: false },
  ],
  promptpay: [
    { key: 'promptpayId', label: 'PromptPay ID', required: true, placeholder: tx('phone number / national ID') },
    { key: 'accountName', label: tx('Account Name'), required: true },
  ],
  credit_card: [{ key: 'cardProvider', label: tx('Card Provider / Gateway'), required: true, placeholder: 'e.g. omise' }],
  cash: [],
}

function summaryOf(m: PaymentMethod): string {
  switch (m.type) {
    case 'bank_transfer':
      return `${m.bankName ?? ''} ${m.accountNumber ?? ''}`.trim() || '—'
    case 'promptpay':
      return m.promptpayId ?? '—'
    case 'credit_card':
      return m.cardProvider ?? '—'
    default:
      return '—'
  }
}

const defaultForm: PaymentMethodPayload = {
  type: 'bank_transfer',
  label: '',
  isActive: true,
  sortOrder: 0,
  instructions: '',
  bankName: '',
  accountName: '',
  accountNumber: '',
  bankBranch: '',
  promptpayId: '',
  promptpayQr: null,
  cardProvider: '',
}

const MAX_QR_BYTES = 500 * 1024 // ~500KB before base64 expansion

// ─── component ────────────────────────────────────────────────────────────────

const PaymentMethodsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [createModal, setCreateModal] = useState(false)
  const [editTarget, setEditTarget] = useState<PaymentMethod | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PaymentMethod | null>(null)
  const [form, setForm] = useState<PaymentMethodPayload>(defaultForm)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => masterDataApi.listPaymentMethods().then((r) => r.data.data),
  })

  const methods: PaymentMethod[] = data ?? []

  const createMutation = useMutation({
    mutationFn: (payload: PaymentMethodPayload) => masterDataApi.createPaymentMethod(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      showToast({ type: 'success', message: tx('Payment method created.') })
      setCreateModal(false)
      setForm(defaultForm)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to create payment method.' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PaymentMethodPayload> }) =>
      masterDataApi.updatePaymentMethod(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      showToast({ type: 'success', message: tx('Payment method updated.') })
      setEditTarget(null)
      setForm(defaultForm)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to update payment method.' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterDataApi.deletePaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
      showToast({ type: 'success', message: tx('Payment method deleted.') })
      setDeleteTarget(null)
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      showToast({ type: 'error', message: err.response?.data?.error ?? 'Failed to delete payment method.' })
    },
  })

  function openCreate() {
    setForm(defaultForm)
    setCreateModal(true)
  }

  function openEdit(m: PaymentMethod) {
    setForm({
      type: m.type,
      label: m.label,
      isActive: m.isActive,
      sortOrder: m.sortOrder,
      instructions: m.instructions ?? '',
      bankName: m.bankName ?? '',
      accountName: m.accountName ?? '',
      accountNumber: m.accountNumber ?? '',
      bankBranch: m.bankBranch ?? '',
      promptpayId: m.promptpayId ?? '',
      promptpayQr: m.promptpayQr ?? null,
      cardProvider: m.cardProvider ?? '',
    })
    setEditTarget(m)
  }

  function validate(): string | null {
    if (!form.label?.trim()) return 'Label is required.'
    for (const f of FIELDS_BY_TYPE[form.type]) {
      if (f.required && !String(form[f.key] ?? '').trim()) return `${f.label} is required.`
    }
    return null
  }

  function handleCreate() {
    const err = validate()
    if (err) {
      showToast({ type: 'error', message: err })
      return
    }
    createMutation.mutate(form)
  }

  function handleUpdate() {
    if (!editTarget) return
    const err = validate()
    if (err) {
      showToast({ type: 'error', message: err })
      return
    }
    updateMutation.mutate({ id: editTarget.id, payload: form })
  }

  // Read an optional QR image file into a base64 data URL (stored inline, no upload).
  function handleQrFile(file: File | undefined) {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', message: tx('Please select an image file.') })
      return
    }
    if (file.size > MAX_QR_BYTES) {
      showToast({ type: 'error', message: tx('QR image too large (max ~500KB).') })
      return
    }
    const reader = new FileReader()
    reader.onload = () => setForm((f) => ({ ...f, promptpayQr: reader.result as string }))
    reader.readAsDataURL(file)
  }

  const columns: Column<PaymentMethod>[] = [
    {
      key: 'label',
      header: tx('Label'),
      sortable: true,
      render: (row) => <span className="font-medium text-gray-900">{row.label}</span>,
    },
    {
      key: 'type',
      header: tx('Type'),
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[row.type]}`}>
          {TYPE_LABELS[row.type]}
        </span>
      ),
    },
    {
      key: 'detail',
      header: tx('Detail'),
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.type === 'promptpay' && row.promptpayQr && (
            <img
              src={row.promptpayQr}
              alt="QR"
              className="h-8 w-8 object-contain border border-gray-200 rounded bg-white"
            />
          )}
          <span className="text-gray-500 text-sm">{summaryOf(row)}</span>
        </div>
      ),
    },
    {
      key: 'isActive',
      header: tx('Status'),
      render: (row) =>
        row.isActive ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            {tx('Active')}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            {tx('Inactive')}
          </span>
        ),
    },
    {
      key: 'sortOrder',
      header: tx('Sort Order'),
      sortable: true,
      render: (row) => <span className="text-gray-500 text-sm">{row.sortOrder}</span>,
    },
    {
      key: 'actions',
      header: tx('Actions'),
      render: (row) => (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {can('master-data/payment-methods.update') && (
            <button
              onClick={() => openEdit(row)}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title={tx('Edit')}
            >
              <Edit2 className="h-4 w-4" />
            </button>
            )}
            {can('master-data/payment-methods.delete') && (
            <button
              onClick={() => setDeleteTarget(row)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={tx('Delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
            )}
          </div>
      ),
    },
  ]

  // Shared form body for create + edit modals — fields change with the selected type.
  function renderFormBody(mode: 'create' | 'edit') {
    return (
      <div className="space-y-4">
        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {tx('Type')} <span className="text-red-500">*</span>
          </label>
          <select
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as PaymentMethodType }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {(Object.keys(TYPE_LABELS) as PaymentMethodType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        {/* Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {tx('Label')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder='e.g. "KBank โอนเงิน"'
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Type-specific fields */}
        {FIELDS_BY_TYPE[form.type].map((f) => (
          <div key={String(f.key)}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} {f.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={String(form[f.key] ?? '')}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        ))}

        {/* PromptPay QR image (optional, promptpay only) */}
        {form.type === 'promptpay' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('QR Code (optional)')}</label>
            {form.promptpayQr && (
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={form.promptpayQr}
                  alt="PromptPay QR preview"
                  className="h-28 w-28 object-contain border border-gray-200 rounded-lg bg-white"
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, promptpayQr: null }))}
                  className="text-sm text-red-600 hover:underline"
                >
                  {tx('Remove')}
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleQrFile(e.target.files?.[0])}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="text-xs text-gray-400 mt-1">PNG/JPG, up to ~500KB. Stored inline (no upload).</p>
          </div>
        )}

        {/* Instructions (all types) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Instructions')}</label>
          <textarea
            rows={2}
            value={form.instructions ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value || null }))}
            placeholder={tx('Optional instructions shown to the payer...')}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        {/* Sort Order */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Sort Order')}</label>
          <input
            type="number"
            value={form.sortOrder ?? 0}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* isActive */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive ?? true}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          />
          <span className="text-sm font-medium text-gray-700">{tx('Active')}</span>
        </label>

        <div className="flex gap-3 mt-1 justify-end">
          <button
            onClick={() => {
              setCreateModal(false)
              setEditTarget(null)
              setForm(defaultForm)
            }}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            {tx('Cancel')}
          </button>
          <button
            onClick={mode === 'create' ? handleCreate : handleUpdate}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {mode === 'create'
              ? createMutation.isPending
                ? 'Creating...'
                : tx('Create')
              : updateMutation.isPending
                ? tx('Saving...')
                : 'Save Changes'}
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) return <LoadingState variant="spinner" />

  if (error) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Payment Methods')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">จัดการวิธีการชำระเงินสำหรับร้านค้า</p>
        </div>
        <EmptyState
          icon={CreditCard}
          title={tx('Failed to load payment methods')}
          description={tx('Please try again.')}
          action={{ label: tx('Retry'), onClick: () => refetch() }}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Payment Methods')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">จัดการวิธีการชำระเงินสำหรับร้านค้า</p>
        </div>
        {can('master-data/payment-methods.create') && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {tx('Add Payment Method')}
          </button>
        )}
      </div>

      {/* Table */}
      {methods.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title={tx('No payment methods yet')}
          description={tx('Add a payment method to get started.')}
        />
      ) : (
        <DataTable columns={columns} data={methods} emptyMessage={tx('No payment methods found.')} />
      )}

      {/* Create modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setCreateModal(false); setForm(defaultForm) }} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Add Payment Method')}</h3>
            {renderFormBody('create')}
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setEditTarget(null); setForm(defaultForm) }} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-gray-900 mb-4">{tx('Edit Payment Method')}</h3>
            {renderFormBody('edit')}
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title={tx('Delete Payment Method')}
        description={`Are you sure you want to delete "${deleteTarget?.label}"? Consider deactivating it instead to preserve payment history.`}
        confirmLabel={tx('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default PaymentMethodsPage
